import React, { useEffect, useState } from 'react';

import TextField from '@material-ui/core/TextField';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Chip, Input, InputAdornment, InputLabel, makeStyles, MenuItem, Select } from '@material-ui/core';
import TitleTwoToneIcon from '@material-ui/icons/TitleTwoTone';
import LanguageIcon from '@material-ui/icons/Language';
import ListAltIcon from '@material-ui/icons/ListAlt';
import Button from '@material-ui/core/Button';
import { useCommonStyles } from '../../styles/common';
import { createOffer, getMerchantDataPasses, getTags } from '../../services/c2dcrm';
import { useAuth } from '../../hooks/useAuth';
import { useHistory } from 'react-router';
import { config } from '../../configs/config';
import { DataPass } from '../../types/dataPassport';
import { Autocomplete } from '@material-ui/lab';
import ReactSelect, { ActionMeta } from 'react-select';

const useStyles = makeStyles(() => ({

  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
}));

type CreateOfferForm = {
  title: string;
  details: string;
  start_date: string;
  end_date?: string;
  offer_url: string;
  status: string;
  data_passes: string[];
  tags: string[];
};

type StateType = {
  passes: DataPass[];
  loading: boolean;
};

const MerchantPerkCreateForm: React.FC = () => {
  const { token } = useAuth();
  const localClasses = useStyles();
  const history = useHistory();
  const classes = useCommonStyles();
  const [dataPassports, setDataPassports] = useState<StateType>({ passes: [], loading: false });
  const [dataPassIds, setDataPassIds] = useState([]);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CreateOfferForm>();
  const [tags, setTags] = useState<string[]>([]);

  const handleDataPassIdChange = (event: any) => {
    setDataPassIds(event.target.value);
  };

  function friendlyDataPassLabel(id: string): string {
    const results = dataPassports.passes.filter(pass => pass.id === id);
    if (results.length === 1) {
      return (`(${results[0].name}) - ${results[0].title}`);
    }
    else {
      return (id);
    }

  }

  useEffect(() => {
    const fetchTags = async () => {
      setTags([]);
      try {
        const response = await getTags(token);
        if (response) setTags(response.data);
      } catch (error) {
        setTags([]);
      }
    };

    fetchTags();
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      setDataPassports({ passes: [], loading: true });
      try {
        const response = await getMerchantDataPasses(token);
        if (response) setDataPassports({ passes: response.data, loading: false });
      } catch (error) {
        setDataPassports({ passes: [], loading: false });
      }
    };


    fetchData();
  }, [token]);

  const onSubmit: SubmitHandler<CreateOfferForm> = (data: CreateOfferForm) => {
    data.start_date = new Date(data.start_date).toISOString();
    if (data.end_date) {
      data.end_date = new Date(data.end_date).toISOString();
    }
    else {
      data.end_date = undefined;
    }
    data.status = 'new';
    data.data_passes = dataPassIds;

    createOffer(token, data).then(() => history.push("/offers"))
  };

  // MISC TEMP
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  // MISC TEMP - END

  return (
    <div className={classes.container}>
      <Typography variant="h1" color="primary">
        Create Your Offer
      </Typography>
      <Card style={{ minWidth: 275, borderRadius: '8px' }}>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              spacing={4}
              className={classes.grid}
            >

              <Grid item xs={12}>
                <InputLabel id="demo-mutiple-name-label">Choose the Pass or Passes for this Offer</InputLabel>
                <Select
                  labelId="demo-mutiple-name-label"
                  id="demo-mutiple-name"
                  label="Title of your Offer"
                  multiple
                  onChange={handleDataPassIdChange}
                  value={dataPassIds}
                  input={<Input />}
                  MenuProps={MenuProps}
                  style={{ width: '100%' }}
                  renderValue={(selected) => (
                    <div className={localClasses.chips}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={friendlyDataPassLabel(value)} className={localClasses.chip} />
                      ))}
                    </div>
                  )}
                >
                  {dataPassports.passes.map((pass) => (
                    <MenuItem key={pass.id} value={pass.id}>
                      {pass.name} {pass.title}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  key="1"
                  id="title"
                  style={{ width: '100%' }}
                  label="Title of your Offer"
                  defaultValue=''
                  {...register('title', { required: true })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleTwoToneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="tags"
                  options={tags}
                  defaultValue={[]}
                  freeSolo
                  {...register('tags', { required: true })}
                  onChange={(e, options) => setValue('tags', options)}
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tags" placeholder="Tags" />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  multiline
                  id="details"
                  defaultValue=''
                  label="Details - Tell your audience about the Offer"
                  style={{ width: '100%' }}
                  {...register('details', { required: true })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListAltIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                {errors.details && <span>This field is required</span>}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  id="start_date"
                  label="Start Date"
                  type="date"
                  defaultValue=''
                  style={{ width: '100%' }}
                  {...register('start_date', { required: true })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />{' '}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  id="end_date"
                  label="End Date"
                  type="date"
                  defaultValue=''
                  style={{ width: '100%' }}
                  {...register('end_date', { required: true })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />{' '}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  key="url"
                  id="url"
                  label="URL for more details"
                  defaultValue=''
                  className={classes.inputWrapper}
                  {...register('offer_url', { required: true })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LanguageIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} style={{ justifyContent: 'center' }}>
                <Button
                  style={{ color: 'white', backgroundColor: config.merchantColour }}
                  type="submit"
                >Create Offer</Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </div>

  );
};

export default MerchantPerkCreateForm;
